import React from 'react';
import ReactDOM from 'react-dom';
import { FaTimes, FaExchangeAlt } from 'react-icons/fa';
import { formatDayDate, getDayDate } from './dashboardUtils';

const getLocationName = (dayTitle, dayNumber) => {
  if (!dayTitle) return `Day ${dayNumber}`;
  return dayTitle.includes(':')
    ? dayTitle.split(':').slice(1).join(':').trim()
    : dayTitle;
};

const SwapDayPicker = ({ isOpen, onClose, sourceDay, allDays = [], startDate, onSelect }) => {
  if (!isOpen || !sourceDay) return null;

  const sourceLabel = getLocationName(sourceDay.dayTitle, sourceDay.day);
  const sourceDate = formatDayDate(getDayDate(startDate, sourceDay.day));

  const candidates = allDays.filter((d) => d.day !== sourceDay.day);

  return ReactDOM.createPortal(
    <div
      className="fixed inset-0 flex items-center justify-center"
      style={{ zIndex: 99999 }}
    >
      <div
        className="absolute inset-0 bg-black/60 backdrop-blur-sm animate-fade-in"
        onClick={onClose}
      />
      <div
        className="relative max-w-md w-full mx-4 bg-white rounded-2xl shadow-floating animate-scale-in overflow-hidden flex flex-col"
        style={{ maxHeight: '85vh' }}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="bg-gradient-to-r from-sky-500 to-indigo-500 px-5 py-4 flex items-center justify-between">
          <div className="flex items-center gap-2 text-white">
            <FaExchangeAlt />
            <h3 className="font-bold text-base">Swap Day {sourceDay.day}</h3>
          </div>
          <button
            onClick={onClose}
            className="text-white/90 hover:text-white p-1 rounded transition-colors"
            aria-label="Close"
          >
            <FaTimes />
          </button>
        </div>

        <div className="px-5 py-3 bg-sky-50 border-b border-sky-100">
          <p className="text-xs text-slate-500 font-medium">Moving</p>
          <p className="text-sm font-semibold text-slate-800 truncate" title={sourceLabel}>
            Day {sourceDay.day} — {sourceLabel}
          </p>
          {sourceDate && <p className="text-xs text-slate-500 mt-0.5">{sourceDate}</p>}
        </div>

        <div className="px-5 pt-3 pb-2">
          <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">
            Select day to swap with
          </p>
          <p className="text-xs text-slate-500 mt-0.5">
            The two days will trade positions in the itinerary.
          </p>
        </div>

        <div className="overflow-y-auto px-3 pb-3 flex-1">
          {candidates.length === 0 ? (
            <div className="text-center py-8 text-slate-400 text-sm">
              No other days available.
            </div>
          ) : (
            <ul className="space-y-2">
              {candidates.map((d) => {
                const targetLabel = getLocationName(d.dayTitle, d.day);
                const targetDate = formatDayDate(getDayDate(startDate, d.day));
                const stops = d.locations?.length || 0;
                return (
                  <li key={d.day}>
                    <button
                      onClick={() => {
                        onSelect(d.day);
                        onClose();
                      }}
                      className="w-full text-left px-3 py-2.5 rounded-lg border border-slate-200 hover:border-sky-400 hover:bg-sky-50 transition-colors flex items-center gap-3 group"
                    >
                      <span className="px-2 py-1 rounded-md bg-slate-100 group-hover:bg-sky-500 group-hover:text-white text-xs font-bold text-slate-700 transition-colors flex-shrink-0">
                        Day {d.day}
                      </span>
                      <span className="flex-1 min-w-0">
                        <span className="block text-sm font-medium text-slate-800 truncate" title={targetLabel}>
                          {targetLabel}
                        </span>
                        <span className="block text-xs text-slate-500">
                          {targetDate}
                          {stops > 0 && ` • ${stops} ${stops === 1 ? 'stop' : 'stops'}`}
                        </span>
                      </span>
                      <FaExchangeAlt className="text-slate-300 group-hover:text-sky-500 transition-colors flex-shrink-0" size={12} />
                    </button>
                  </li>
                );
              })}
            </ul>
          )}
        </div>

        <div className="px-5 py-3 border-t border-slate-100 bg-slate-50 flex justify-end">
          <button
            onClick={onClose}
            className="px-4 py-2 text-sm font-medium text-slate-600 hover:text-slate-800 rounded-lg hover:bg-slate-200 transition-colors"
          >
            Cancel
          </button>
        </div>
      </div>
    </div>,
    document.body
  );
};

export default SwapDayPicker;
